import { baseApi } from "./baseApi";

export type PaymentStatus = "successful" | "pending" | "under_review" | "failed";

export interface VerifyPaymentResponse {
    status: string;
    data: {
        payment_status: PaymentStatus;
        message: string;
        plan?: string;
        type?: string;
        reference: string;
    };
}

export type PlanResource = "users" | "branches" | "services" | "fleets";

export interface PlanLimitUsage {
    allowed: boolean;
    limit: number | null; // null = unlimited
    current: number;
    reason?: string | null;
    upgrade_url?: string | null;
}

export const subscriptionApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({

        /* 
        get company subscriptions (paginated)
        */
        getCompanySubscriptions: builder.query<
            any,
            { companyId: string; page?: number }
        >({
            query: ({ companyId, page = 1 }) => ({
                url: `/subscription/company/${companyId}/subscription?page=${page}`,
                method: "GET",
            }),
        }),

        /* 
        get all subscription plans
        */
        getAllSubscriptionPlans: builder.query<any, void>({
            query: () => ({
                url: `/subscription/plans`,
                method: "GET",
            }),
        }),

        /* 
        get single plan details
        */
        getSubscriptionPlan: builder.query<any, { id: string }>({
            query: ({ id }) => ({
                url: `/subscription/plans/${id}`,
                method: "GET",
            }),
        }),

        /* 
        get company subscription by id
        */
        getCompanySubscriptionById: builder.query<
            any,
            { companySubscriptionId: string }
        >({
            query: ({ companySubscriptionId }) => ({
                url: `/subscription/subscription/${companySubscriptionId}`,
                method: "GET",
            }),
        }),

        /* 
        cancel subscription
        */
        cancelSubscription: builder.mutation<
            any,
            { subscriptionId: string; reason: string }
        >({
            query: ({ subscriptionId, reason }) => ({
                url: `/subscription/subscriptions/${subscriptionId}/cancel`,
                method: "POST",
                body: { reason },
            }),
        }),

        /* 
        renew subscription
        */
        renewSubscription: builder.mutation<
            any,
            { companySubscriptionId: string; plan_id: string, billing_cycle: string }
        >({
            query: ({ companySubscriptionId, plan_id, billing_cycle }) => ({
                url: `/subscription/renew/${companySubscriptionId}`,
                method: "POST",
                body: { plan_id, billing_cycle },
            }),
            invalidatesTags: ["users"],
        }),

        /* 
        upgrade subscription
        */
        upgradeSubscription: builder.mutation<
            any,
            { companyId: string; plan_id: string; billing_cycle: string }
        >({
            query: ({ companyId, plan_id, billing_cycle }) => ({
                url: `/subscription/upgrade/${companyId}`,
                method: "POST",
                body: { plan_id, billing_cycle },
            }),
            invalidatesTags: ["users"],
        }),

        /*
        plan usage for one resource (the signed-in user's own company);
        refreshed whenever something that counts toward the limit is added or removed
        */
        checkPlanLimit: builder.query<PlanLimitUsage, PlanResource>({
            query: (resource) => ({
                url: `/subscription/check-limit?resource=${resource}`,
                method: "GET",
            }),
            transformResponse: (response: any) => response?.data,
            providesTags: ["planLimit"],
        }),

        /*
        verify a RoutePay payment after checkout redirects back with ?reference=
        */
        verifySubscriptionPayment: builder.query<VerifyPaymentResponse, string>({
            query: (reference) => ({
                url: `/subscription/payment/${encodeURIComponent(reference)}/verify`,
                method: "GET",
            }),
        }),
    }),
});

export const {
    useGetCompanySubscriptionsQuery,
    useGetAllSubscriptionPlansQuery,
    useGetSubscriptionPlanQuery,
    useGetCompanySubscriptionByIdQuery,
    useCancelSubscriptionMutation,
    useRenewSubscriptionMutation,
    useUpgradeSubscriptionMutation,
    useLazyVerifySubscriptionPaymentQuery,
    useCheckPlanLimitQuery,
} = subscriptionApi;
